import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";

import { setContext } from "@apollo/client/link/context";
import { useAuth0 } from "@auth0/auth0-react";
import { EIGHT_BASE } from "../../config-global";
import { getMainDefinition } from "@apollo/client/utilities";
// import { WebSocketLink } from "@apollo/client/link/ws";
import { WebSocketLink } from "apollo-link-ws";
import { onError } from "@apollo/client/link/error";
import { isValidToken, setSession } from "../auth/utils";

// Global reference to WebSocket subscription client for status monitoring
export let wsSubscriptionClient = null;

const defaultOptions = {
  watchQuery: {
    // fetchPolicy: "no-cache",
    fetchPolicy: "cache-and-network",
    // fetchPolicy: "network-only",
    errorPolicy: "all",
  },
  query: {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  },
  mutation: {
    errorPolicy: "all",
  },
};

const ApolloViewModel = ({ children }) => {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const client = React.useRef();
  const AUTH_RETURN_PATH_KEY = "auth0_return_path";
  const INVALID_TOKEN_FLAG_KEY = "hh_invalid_token_dialog";
  const INVALID_TOKEN_REASON_KEY = "hh_invalid_token_reason";
  const INVALID_TOKEN_EVENT = "hh-auth-invalid-token";

  const showInvalidTokenDialog = useCallback((reason) => {
    // Dedupe only this dialog path — do NOT use session_invalid_handled here;
    // that flag is set by full logout redirects and would block the dialog forever
    // in the same tab.
    try {
      if (sessionStorage.getItem(INVALID_TOKEN_FLAG_KEY) === "1") return;
    } catch (e) {
      // ignore
    }

    // Clear known auth-related cached values (so we don't keep firing bad requests).
    try {
      setSession(null);
      localStorage.removeItem("user");
      localStorage.removeItem("outlook_access_token");
      localStorage.removeItem("outlook_refresh_token");
      localStorage.removeItem("google_access_token");
      localStorage.removeItem("google_refresh_token");
      localStorage.removeItem("ring_central_token");
      localStorage.removeItem("ring_central_refresh_token");
      localStorage.removeItem("ring_central_user_data");
    } catch (e) {
      // ignore
    }

    // Preserve the route so after re-login we can send the user back.
    try {
      const pathname = window.location.pathname;
      if (pathname && !pathname.startsWith("/auth")) {
        sessionStorage.setItem(AUTH_RETURN_PATH_KEY, pathname);
      }
    } catch (e) {
      // ignore
    }

    try {
      sessionStorage.setItem(INVALID_TOKEN_FLAG_KEY, "1");
      sessionStorage.setItem(INVALID_TOKEN_REASON_KEY, reason || "");
      window.dispatchEvent(new Event(INVALID_TOKEN_EVENT));
    } catch (e) {
      // ignore
    }
  }, []);

  const invalidateWebSession = useCallback((reason) => {
    // Prevent redirect/reload loops caused by repeated auth errors.
    const alreadyHandled = sessionStorage.getItem("session_invalid_handled") === "1";
    if (alreadyHandled) return;
    sessionStorage.setItem("session_invalid_handled", "1");

    // Clear known auth-related cached values.
    try {
      setSession(null); // clears accessToken + axios header
      localStorage.removeItem("user");
      localStorage.removeItem("outlook_access_token");
      localStorage.removeItem("outlook_refresh_token");
      localStorage.removeItem("google_access_token");
      localStorage.removeItem("google_refresh_token");
      localStorage.removeItem("ring_central_token");
      localStorage.removeItem("ring_central_refresh_token");
      localStorage.removeItem("ring_central_user_data");
    } catch (e) {
      // Best-effort cleanup; continue with redirect.
      console.warn("Session cleanup failed:", e);
    }

    console.warn("Session invalidated:", reason);

    // Hard logout: clear Auth0 cache/session as well.
    // LogoutInitPage triggers Auth0 logout and then returns to /auth/login.
    try {
      const pathname = window.location.pathname;
      if (pathname && !pathname.startsWith("/auth")) {
        sessionStorage.setItem(AUTH_RETURN_PATH_KEY, pathname);
      }
    } catch (e) {
      // ignore
    }

    // Send user to /logout (real route) which will perform Auth0 logout if needed.
    window.location.replace("/logout");
  }, []);

  const getToken = useCallback(async () => {
    let token = null;
    if (isAuthenticated) {
      const claim = await getIdTokenClaims();
      token = claim.__raw;
    } else {
      token = localStorage.getItem("accessToken");
    }

    if (token && !isValidToken(token)) {
      invalidateWebSession("Stored accessToken expired/invalid");
      return null;
    }

    return token;
  }, [getIdTokenClaims, invalidateWebSession, isAuthenticated]);

  const customToken = window.location.pathname.includes("careers")
    ? EIGHT_BASE.applicantToken
    : localStorage.getItem("accessToken");
  const httpLink = createHttpLink({
    uri: EIGHT_BASE.graphqlApiUrl,
    headers: {
      Authorization: `Bearer ${customToken}`,
    },
  });

  const authLink = setContext(async (_, { headers, ...rest }) => {
    let token;
    try {
      token = await getToken();
    } catch (error) {
      console.log(error);
    }

    if (!token) return { headers, ...rest };

    return {
      ...rest,
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    };
  });
// 1728000
  const isTokenValidationError = (item) => {
    if (!item) return false;
    const msg = String(item?.message || "");
    const msgLower = msg.toLowerCase();
    const extCode = item?.extensions?.code;
    const topCode = item?.code;
    const excCode =
      item?.extensions?.exception?.code ||
      item?.extensions?.exception?.name;
    return (
      msgLower.includes("token validation") ||
      extCode === "InvalidTokenError" ||
      extCode === "InvalidToken" ||
      topCode === "InvalidTokenError" ||
      topCode === "InvalidToken" ||
      excCode === "InvalidTokenError"
    );
  };

  const errorHandle = onError(({ graphQLErrors, networkError }) => {
    const fromNetwork = networkError?.result?.errors;
    const combined = [
      ...(graphQLErrors || []),
      ...(Array.isArray(fromNetwork) ? fromNetwork : []),
    ];

    const tokenValidationItem = combined.find((item) => isTokenValidationError(item));
    if (tokenValidationItem) {
      showInvalidTokenDialog(
        tokenValidationItem.message || "Token validation"
      );
      return;
    }

    combined.forEach((item) => {
      const msg = item?.message || "";
      const msgLower = typeof msg === "string" ? msg.toLowerCase() : "";

      if (msg === "Token expired") {
        invalidateWebSession("GraphQL: Token expired");
        return;
      }

      if (
        item?.extensions?.code === "UNAUTHENTICATED" ||
        msgLower.includes("unauthorized")
      ) {
        invalidateWebSession(`GraphQL: ${msg}`);
      }
    });

    // Network-level auth failures (e.g. 401/403)
    const statusCode = networkError?.statusCode || networkError?.status || networkError?.response?.status;
    if (statusCode === 401 || statusCode === 403) {
      invalidateWebSession(`Network: HTTP ${statusCode}`);
    }
  });

  const wsLink = new WebSocketLink({
    uri: "wss://ws.8base.com",
    options: {
      connectionParams: async () => {
        // Use the socket token if available, otherwise fallback to API token
        const token = EIGHT_BASE.eightBaseSocketApiToken || EIGHT_BASE.eightBaseApiToken;
        
        // Use environment from config (should match mobile app environment)
        // Mobile uses: isProduction ? 'master' : 'site_task'
        // Web should use: REACT_APP_ENVIRONMENT from .env
        const environmentName = EIGHT_BASE.eightBaseEnvironment || 'site_task';
        
        console.log('🔐 WebSocket connection params:', {
          workspaceId: EIGHT_BASE.eightBaseWorkspace,
          environmentName,
          environmentFromConfig: EIGHT_BASE.eightBaseEnvironment,
          tokenLength: token?.length || 0,
          hasSocketToken: !!EIGHT_BASE.eightBaseSocketApiToken,
          hasApiToken: !!EIGHT_BASE.eightBaseApiToken
        });
        
        const params = {
          workspaceId: EIGHT_BASE.eightBaseWorkspace,
          environmentName,
          token,
        };
        
        console.log('🔌 WebSocket connecting with params:', { 
          workspaceId: params.workspaceId, 
          environmentName: params.environmentName,
          hasToken: !!params.token,
          CRITICAL: '⚠️ Environment MUST match mobile app for subscriptions to work!'
        });
        
        return params;
      },
      connectionCallback: async (errors) => {
        if (errors) {
          console.error('❌ WebSocket connection error:', errors);
          // Don't close on error, let it retry
        } else {
          console.log('✅ WebSocket connected successfully!');
          // Keep-alive is handled automatically by subscriptions-transport-ws
        }
      },
      reconnect: true,
      reconnectionAttempts: 10,
      timeout: 30000,
      lazy: false, // Connect immediately instead of waiting for first subscription
      inactivityTimeout: 0, // Disable inactivity timeout to keep connection alive
      keepAlive: 30000, // Send keep-alive ping every 30 seconds
    },
    webSocketImpl: class WebSocketWithoutProtocol extends WebSocket {
      constructor(url) {
        console.log('🌐 Creating WebSocket connection to:', url);
        super(url); // ignore protocol
      }
    },
  });
  
  // Store global reference for status monitoring
  if (wsLink.subscriptionClient) {
    wsSubscriptionClient = wsLink.subscriptionClient;
    console.log('✅ WebSocket subscription client stored globally');
  }
  
  // Add WebSocket event listeners for debugging
  if (wsLink.subscriptionClient) {
    wsLink.subscriptionClient.on('connecting', () => {
      console.log('🔄 WebSocket connecting...');
    });
    
    wsLink.subscriptionClient.on('connected', () => {
      console.log('✅ WebSocket connected!');
    });
    
    wsLink.subscriptionClient.on('reconnecting', () => {
      console.log('🔄 WebSocket reconnecting...');
    });
    
    wsLink.subscriptionClient.on('reconnected', () => {
      console.log('✅ WebSocket reconnected!');
    });
    
    wsLink.subscriptionClient.on('disconnected', () => {
      console.log('❌ WebSocket disconnected');
    });
    
    wsLink.subscriptionClient.on('error', (error) => {
      // Suppress "Invalid message type!" errors as they're often harmless
      // and can occur when the server sends unsupported message types
      if (error?.message?.includes('Invalid message type')) {
        console.log('⚠️ Suppressing harmless "Invalid message type" error');
        return;
      }
      console.error('❌ WebSocket error:', error);
      console.error('❌ Error details:', JSON.stringify(error, null, 2));
    });
    
    // Log all messages for debugging
    wsLink.subscriptionClient.on('data', (data) => {
      console.log('📨 📨 📨 WebSocket RAW data received:', data);
      console.log('📨 Data type:', typeof data);
      console.log('📨 Data content:', JSON.stringify(data, null, 2));
    });
    
    // Log when subscription is sent
    wsLink.subscriptionClient.on('subscription_success', (data) => {
      console.log('✅ Subscription registered successfully:', data);
    });
    
    wsLink.subscriptionClient.on('subscription_fail', (data) => {
      console.error('❌ Subscription registration failed:', data);
    });
    
    // Override processReceivedData to catch and suppress invalid message type errors
    const originalProcessReceivedData = wsLink.subscriptionClient.processReceivedData;
    if (originalProcessReceivedData) {
      wsLink.subscriptionClient.processReceivedData = function(data) {
        try {
          return originalProcessReceivedData.call(this, data);
        } catch (error) {
          // Suppress "Invalid message type!" errors
          if (error?.message?.includes('Invalid message type')) {
            // Silently ignore - this is often a server-side protocol issue
            return;
          }
          // Re-throw other errors
          throw error;
        }
      };
    }
  }

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      const isSubscription = (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
      
      if (isSubscription) {
        console.log('🔀 Routing to WebSocket (subscription detected):', {
          operationName: definition.name?.value,
          operation: definition.operation
        });
      }
      
      return isSubscription;
    },
    wsLink,
    httpLink
  );

  client.current = new ApolloClient({
    link: ApolloLink.from([errorHandle, authLink, splitLink]),
    cache: new InMemoryCache(),
    connectToDevTools: process.env.REACT_APP_ENV === "local",
    defaultOptions,
  });

  return <ApolloProvider client={client.current}>{children}</ApolloProvider>;
};

ApolloViewModel.propTypes = {
  children: PropTypes.node,
};

export { ApolloViewModel };
